import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import ZoomVideo, {
  VideoQuality,
  type VideoPlayer,
} from "@zoom/videosdk";
import { message } from "antd";

import { useZoom } from "../zoom/ZoomProvider";
import {
  devicePrefsEventName,
  loadDevicePrefs,
  saveDevicePrefs,
} from "../zoom/devicePrefs";
import { useVideoLayoutManager } from "../zoom/useVideoLayoutManager";
import { isConsultEnded } from "../zoom/telehealthGuard";

import VideoFooter from "../components/VideoFooter";
import RemoteViewControl from "../components/controls/RemoteViewControl";

import {
  useLoadingContext,
  useRoomCacheContext,
} from "../../context/roomContext";

import type {
  MeetingConfig,
  RoomCache,
} from "../../context/types";

import "../styles/video.scss";

type Participant = {
  userId: number;
  displayName?: string;
  bVideoOn?: boolean;
};

type PersistedJoin = {
  meetingConfig: MeetingConfig;
  roomCache: RoomCache;
  savedAt: number;
};

type PanPosition = {
  x: number;
  y: number;
};

type DragState = {
  active: boolean;
  startX: number;
  startY: number;
  startPanX: number;
  startPanY: number;
  currentPanX: number;
  currentPanY: number;
};

type CameraDevice = {
  deviceId: string;
  label?: string;
};

type RemoteVideoMountProps = {
  userId: number;
  register: (
    userId: number,
    element: HTMLDivElement | null
  ) => void;
  style: React.CSSProperties;
};

const MAX_JOIN_AGE_MS = 6 * 60 * 60 * 1000;
const MAX_ATTACH_RETRIES = 15;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function delay(milliseconds: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

function isMobileDevice() {
  const userAgent = navigator.userAgent || "";

  return /Android|iPhone|iPad|iPod/i.test(userAgent);
}

function isIOSDevice() {
  const userAgent = navigator.userAgent || "";

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function errorToLogValue(error: unknown) {
  if (!error) return error;

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === "object") {
    try {
      return JSON.parse(JSON.stringify(error));
    } catch {
      return String(error);
    }
  }

  return String(error);
}

function isFrontCamera(camera: CameraDevice) {
  const label = (camera.label || "").toLowerCase();

  return (
    label.includes("front") ||
    label.includes("user") ||
    label.includes("selfie") ||
    label.includes("facetime") ||
    label.includes("face")
  );
}

function isZoomVideoPlayer(value: unknown): value is VideoPlayer {
  return (
    value instanceof HTMLElement &&
    value.tagName.toLowerCase() === "video-player"
  );
}


function serialiseDebugValue(value: unknown) {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (value instanceof Error) {
    return JSON.stringify(
      {
        name: value.name,
        message: value.message,
        stack: value.stack,
      },
      null,
      2
    );
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const RemoteVideoMount = React.memo(function RemoteVideoMount({
  userId,
  register,
  style,
}: RemoteVideoMountProps) {
  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      register(userId, element);
    },
    [register, userId]
  );

  return <div ref={ref} style={style} />;
});

export default function Consult() {
  const { cid } = useParams();
  const navigate = useNavigate();

  const {
    meetingConfig,
    roomCache,
  } = useRoomCacheContext();

  const {
    setShowLoading,
  } = useLoadingContext();

  const { client, mediaStream, status, joinOnce } = useZoom();

  /*
   * Initial audio/video preferences remain stable during this consult.
   * Remote display preferences are reactive below.
   */
  const initialPrefs = useMemo(() => loadDevicePrefs(), []);


  const debugEnabled = useMemo(() => {
    return new URLSearchParams(window.location.search).get("debug") === "1";
  }, []);

  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [debugPanelCollapsed, setDebugPanelCollapsed] = useState(false);


  const [persisted, setPersisted] =
    useState<PersistedJoin | null>(null);

  const [remoteFit, setRemoteFit] =
    useState<"contain" | "cover">(
      loadDevicePrefs().remoteFitMode ?? "contain"
    );

  const [remoteZoom, setRemoteZoom] = useState(
    clamp(loadDevicePrefs().remoteZoom ?? 1, 1, 3)
  );

  const [remotePan, setRemotePan] = useState<PanPosition>({
    x: loadDevicePrefs().remotePanX ?? 0,
    y: loadDevicePrefs().remotePanY ?? 0,
  });

  const [remotes, setRemotes] = useState<Participant[]>([]);
  const [selfVideoOn, setSelfVideoOn] = useState(false);
  const [attachRetryVersion, setAttachRetryVersion] =
    useState(0);

  /*
   * When this device joins an already-active consult, Zoom can report an
   * existing participant as video-on before the receiving subscription is
   * fully ready. A one-time forced detach/reattach refreshes those initial
   * streams without waiting for the sender to toggle their camera.
   */
  const [initialRemoteRefreshVersion, setInitialRemoteRefreshVersion] =
    useState(0);

  const initialRemoteRefreshScheduledRef = useRef(false);
  const forceRemoteReattachIdsRef = useRef(new Set<number>());

  /*
   * Incremented when a real Zoom <video-player> element mounts.
   * This is important when joining an already-active consult because the
   * participant list can arrive before React has mounted the player elements.
   */
  const [playerMountVersion, setPlayerMountVersion] =
    useState(0);

  const playerRefs =
    useRef<Record<string, VideoPlayer | null>>({});

  const attachedSelfElementRef =
    useRef<VideoPlayer | null>(null);

  /*
   * React owns stable ordinary <div> mount points for remote participants.
   * Zoom owns the <video-player> elements appended inside those mount points.
   */
  const remoteTileRefsRef = useRef(
    new Map<number, HTMLDivElement>()
  );

  const attachedRemotePlayersRef = useRef(
    new Map<number, VideoPlayer>()
  );

  const attachingRemoteUsersRef = useRef(new Set<number>());
  const attachmentGenerationRef =
    useRef(new Map<number, number>());

  const attachRetryCountRef =
    useRef(new Map<number, number>());

  const attachRetryTimersRef =
    useRef(new Map<number, number>());

  const joinAttemptedRef = useRef(false);
  const remoteStageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const mountedRef = useRef(true);

  const selfId = client?.getSessionInfo?.()?.userId as
    | number
    | undefined;

  /*
   * These wrappers use the runtime SDK methods directly.
   * This avoids problems if a custom MediaStream declaration does not yet
   * contain the SDK 2.4.5 element-specific overloads.
   */
  const attachSelfVideoToElement = useCallback(
    async (
      userId: number,
      quality: VideoQuality,
      element: VideoPlayer
    ) => {
      if (!mediaStream) return;

      const startedAt = performance.now();

      console.log("[Zoom attach self start]", {
        userId,
        quality,
        elementConnected: element.isConnected,
        elementWidth: element.clientWidth,
        elementHeight: element.clientHeight,
      });

      try {
        const result = await (mediaStream as any).attachVideo(
          userId,
          quality,
          element
        );

        console.log("[Zoom attach self success]", {
          userId,
          quality,
          elapsedMs: Math.round(performance.now() - startedAt),
          result,
        });

        return result;
      } catch (error) {
        console.error("[Zoom attach self failed]", {
          userId,
          quality,
          elapsedMs: Math.round(performance.now() - startedAt),
          error: errorToLogValue(error),
        });

        throw error;
      }
    },
    [mediaStream]
  );

  const attachRemoteVideoPlayer = useCallback(
    async (
      userId: number,
      quality: VideoQuality
    ): Promise<VideoPlayer | null> => {
      if (!mediaStream) {
        throw new Error("Zoom media stream is not available");
      }

      const startedAt = performance.now();

      console.log("[Zoom attach remote start]", {
        userId,
        quality,
        supportsMultipleVideos:
          mediaStream.isSupportMultipleVideos?.(),
      });

      try {
        const result = await (mediaStream as any).attachVideo(
          userId,
          quality
        );

        if (!isZoomVideoPlayer(result)) {
          console.warn("[Zoom attach remote not ready]", {
            userId,
            quality,
            elapsedMs: Math.round(performance.now() - startedAt),
            result: errorToLogValue(result),
            currentUser: (
              client.getAllUser?.() || []
            ).find(
              (user: Participant) => user.userId === userId
            ),
            supportsMultipleVideos:
              mediaStream.isSupportMultipleVideos?.(),
          });

          return null;
        }

        console.log("[Zoom attach remote success]", {
          userId,
          quality,
          elapsedMs: Math.round(performance.now() - startedAt),
          playerConnected: result.isConnected,
          supportsMultipleVideos:
            mediaStream.isSupportMultipleVideos?.(),
        });

        return result;
      } catch (error) {
        console.error("[Zoom attach remote failed]", {
          userId,
          quality,
          elapsedMs: Math.round(performance.now() - startedAt),
          error: errorToLogValue(error),
          supportsMultipleVideos:
            mediaStream.isSupportMultipleVideos?.(),
        });

        throw error;
      }
    },
    [client, mediaStream]
  );

  const detachVideoFromElement = useCallback(
    async (
      userId: number,
      element?: VideoPlayer | null
    ) => {
      if (!mediaStream) return;

      console.log("[Zoom detachVideo start]", {
        userId,
        hasElement: !!element,
        elementConnected: element?.isConnected,
      });

      try {
        const result = element
          ? await (mediaStream as any).detachVideo(userId, element)
          : await (mediaStream as any).detachVideo(userId);

        console.log("[Zoom detachVideo success]", {
          userId,
          hasElement: !!element,
          result,
        });

        return result;
      } catch (error) {
        console.error("[Zoom detachVideo failed]", {
          userId,
          hasElement: !!element,
          error: errorToLogValue(error),
        });

        throw error;
      }
    },
    [mediaStream]
  );

  const joinKey = useCallback(
    (consultId: string) =>
      `telehealth_consult_join_v1:${consultId}`,
    []
  );

  const readGuestAttendee = useCallback(
    (consultId: string) => {
      try {
        const raw = sessionStorage.getItem(
          `telehealth_guest_attendee_v1:${consultId}`
        );

        if (!raw) return null;

        const parsed = JSON.parse(raw);

        if (!parsed?.name || !parsed?.relationship) {
          return null;
        }

        return {
          name: String(parsed.name),
          relationship: String(parsed.relationship),
        };
      } catch {
        return null;
      }
    },
    []
  );

  const readPersisted = useCallback(
    (consultId: string): PersistedJoin | null => {
      try {
        const raw = sessionStorage.getItem(
          joinKey(consultId)
        );

        if (!raw) return null;

        const parsed = JSON.parse(raw) as PersistedJoin;

        if (
          !parsed?.meetingConfig ||
          !parsed?.roomCache
        ) {
          return null;
        }

        if (
          Date.now() - (parsed.savedAt ?? 0) >
          MAX_JOIN_AGE_MS
        ) {
          return null;
        }

        return parsed;
      } catch {
        return null;
      }
    },
    [joinKey]
  );

  const writePersisted = useCallback(
    (
      consultId: string,
      payload: PersistedJoin
    ) => {
      try {
        sessionStorage.setItem(
          joinKey(consultId),
          JSON.stringify(payload)
        );
      } catch {
        // Session storage can be unavailable in restricted modes.
      }
    },
    [joinKey]
  );

  const getRemoteGridStyle = useCallback(
    (count: number): React.CSSProperties => {
      const isNarrow = window.innerWidth < 700;

      let columns = 1;

      if (count === 1) {
        columns = 1;
      } else if (count === 2) {
        columns = isNarrow ? 1 : 2;
      } else if (count <= 4) {
        columns = 2;
      } else {
        columns = isNarrow ? 2 : 3;
      }

      const rows = Math.ceil(count / columns);

      return {
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        width: "100%",
        height: "100%",
        gap: 10,
        padding: 10,
        boxSizing: "border-box",
        overflow: "hidden",
      };
    },
    []
  );


  /*
   * When ?debug=1 is present, mirror console output into an on-screen panel.
   * This makes Zoom diagnostics available directly on iPhone/Safari.
   */
  useEffect(() => {
    if (!debugEnabled) return;

    const originalLog = console.log.bind(console);
    const originalWarn = console.warn.bind(console);
    const originalError = console.error.bind(console);

    const append = (
      level: "LOG" | "WARN" | "ERROR",
      args: unknown[]
    ) => {
      const entry = [
        `${new Date().toISOString()} [${level}]`,
        ...args.map(serialiseDebugValue),
      ].join("\n");

      setDebugLogs((current) => [
        ...current.slice(-299),
        entry,
      ]);
    };

    console.log = (...args: unknown[]) => {
      originalLog(...args);
      append("LOG", args);
    };

    console.warn = (...args: unknown[]) => {
      originalWarn(...args);
      append("WARN", args);
    };

    console.error = (...args: unknown[]) => {
      originalError(...args);
      append("ERROR", args);
    };

    originalLog("[On-screen diagnostics enabled]", {
      href: window.location.href,
      userAgent: navigator.userAgent,
    });

    append("LOG", ["[On-screen diagnostics enabled]", {
      href: window.location.href,
      userAgent: navigator.userAgent,
    }]);

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, [debugEnabled]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /*
   * Persist the meeting details for browser refresh/rejoin.
   */
  useEffect(() => {
    if (!cid) return;

    if (meetingConfig && roomCache) {
      const payload: PersistedJoin = {
        meetingConfig,
        roomCache,
        savedAt: Date.now(),
      };

      writePersisted(cid, payload);
      setPersisted(payload);
      return;
    }

    const stored = readPersisted(cid);

    if (stored) {
      setPersisted(stored);
    }
  }, [
    cid,
    meetingConfig,
    roomCache,
    readPersisted,
    writePersisted,
  ]);

  const effectiveMeetingConfig =
    meetingConfig ?? persisted?.meetingConfig;

  const effectiveRoomCache =
    roomCache ?? persisted?.roomCache;

  /*
   * Keep remote fit, zoom and pan settings reactive.
   */
  useEffect(() => {
    const syncRemotePreferences = () => {
      const preferences = loadDevicePrefs();

      setRemoteFit(
        (preferences.remoteFitMode ?? "contain") as
          | "contain"
          | "cover"
      );

      setRemoteZoom(
        clamp(preferences.remoteZoom ?? 1, 1, 3)
      );

      setRemotePan({
        x: preferences.remotePanX ?? 0,
        y: preferences.remotePanY ?? 0,
      });
    };

    const onStorage = (event: StorageEvent) => {
      if (
        event.key === "telehealth_device_prefs_v1"
      ) {
        syncRemotePreferences();
      }
    };

    syncRemotePreferences();

    window.addEventListener(
      devicePrefsEventName,
      syncRemotePreferences
    );

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener(
        devicePrefsEventName,
        syncRemotePreferences
      );

      window.removeEventListener(
        "storage",
        onStorage
      );
    };
  }, []);

  const layout = useVideoLayoutManager({
    footerHeightPx: 72,
    gapPx: 10,
    fitMode: initialPrefs.pipFitMode ?? "cover",
  });

  const incrementAttachmentGeneration =
    useCallback((userId: number) => {
      const nextGeneration =
        (attachmentGenerationRef.current.get(userId) ??
          0) + 1;

      attachmentGenerationRef.current.set(
        userId,
        nextGeneration
      );

      return nextGeneration;
    }, []);

  const clearAttachRetry = useCallback(
    (userId: number) => {
      const timer =
        attachRetryTimersRef.current.get(userId);

      if (timer !== undefined) {
        window.clearTimeout(timer);
        attachRetryTimersRef.current.delete(userId);
      }

      attachRetryCountRef.current.delete(userId);
    },
    []
  );

  const scheduleAttachRetry = useCallback(
    (userId: number) => {
      if (!mountedRef.current) return;

      if (
        attachRetryTimersRef.current.has(userId)
      ) {
        return;
      }

      const attempts =
        attachRetryCountRef.current.get(userId) ?? 0;

      if (attempts >= MAX_ATTACH_RETRIES) {
        console.warn(
          `Video attachment retries exhausted for participant ${userId}`
        );

        return;
      }

      attachRetryCountRef.current.set(
        userId,
        attempts + 1
      );

      const wait =
        attempts < 3
          ? 250
          : Math.min(
              500 + attempts * 250,
              2500
            );

      const timer = window.setTimeout(() => {
        attachRetryTimersRef.current.delete(userId);

        if (mountedRef.current) {
          setAttachRetryVersion(
            (version) => version + 1
          );
        }
      }, wait);

      attachRetryTimersRef.current.set(
        userId,
        timer
      );
    },
    []
  );

  /*
   * Register the stable React mount point for a remote participant.
   * Zoom creates the actual <video-player> and the attachment effect appends
   * it into this mount point after both the participant and tile exist.
   */
  const registerRemoteTileRef = useCallback(
    (
      userId: number,
      element: HTMLDivElement | null
    ) => {
      const previousElement =
        remoteTileRefsRef.current.get(userId);

      if (previousElement === element) {
        return;
      }

      if (element) {
        remoteTileRefsRef.current.set(userId, element);
      } else {
        remoteTileRefsRef.current.delete(userId);
      }

      console.log("[Zoom remote tile ref]", {
        userId,
        mounted: !!element,
        elementConnected: element?.isConnected,
        elementWidth: element?.clientWidth,
        elementHeight: element?.clientHeight,
      });

      incrementAttachmentGeneration(userId);
      attachingRemoteUsersRef.current.delete(userId);
      clearAttachRetry(userId);

      if (element && element.isConnected) {
        setPlayerMountVersion((version) => version + 1);
      }
    },
    [
      clearAttachRetry,
      incrementAttachmentGeneration,
    ]
  );

  /*
   * Register the currently mounted self player.
   *
   * The main attachment effect performs any required detach and reattach.
   */
  const registerSelfPlayerRef = useCallback(
    (element: VideoPlayer | null) => {
      if (!selfId) return;

      const key = String(selfId);
      const previousElement =
        playerRefs.current[key];

      if (previousElement === element) {
        return;
      }

      playerRefs.current[key] = element;

      console.log("[Zoom self player ref]", {
        selfId,
        mounted: !!element,
        elementConnected: element?.isConnected,
        elementWidth: element?.clientWidth,
        elementHeight: element?.clientHeight,
      });

      if (element && element.isConnected) {
        setPlayerMountVersion((version) => version + 1);
      }
    },
    [selfId]
  );

  const getPanBounds = useCallback(() => {
    const element = remoteStageRef.current;

    if (!element) {
      return {
        maxX: 0,
        maxY: 0,
      };
    }

    const rect = element.getBoundingClientRect();

    return {
      maxX:
        ((remoteZoom - 1) * rect.width) / 2,
      maxY:
        ((remoteZoom - 1) * rect.height) / 2,
    };
  }, [remoteZoom]);

  const clampPan = useCallback(
    (x: number, y: number): PanPosition => {
      const { maxX, maxY } = getPanBounds();

      return {
        x: clamp(x, -maxX, maxX),
        y: clamp(y, -maxY, maxY),
      };
    },
    [getPanBounds]
  );

  const onRemotePointerDown = useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>
    ) => {
      if (remotes.length !== 1) return;
      if (remoteZoom <= 1.0001) return;

      event.currentTarget.setPointerCapture?.(
        event.pointerId
      );

      dragRef.current = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        startPanX: remotePan.x,
        startPanY: remotePan.y,
        currentPanX: remotePan.x,
        currentPanY: remotePan.y,
      };
    },
    [
      remotes.length,
      remotePan.x,
      remotePan.y,
      remoteZoom,
    ]
  );

  const onRemotePointerMove = useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>
    ) => {
      const drag = dragRef.current;

      if (!drag?.active) return;
      if (remotes.length !== 1) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      const nextPan = clampPan(
        drag.startPanX + dx,
        drag.startPanY + dy
      );

      drag.currentPanX = nextPan.x;
      drag.currentPanY = nextPan.y;

      setRemotePan(nextPan);
    },
    [clampPan, remotes.length]
  );

  const endRemoteDrag = useCallback(() => {
    const drag = dragRef.current;

    if (!drag?.active) return;

    drag.active = false;

    saveDevicePrefs({
      remotePanX: drag.currentPanX,
      remotePanY: drag.currentPanY,
    });
  }, []);

  /*
   * Clear connection-specific state when Zoom reports a closed session.
   */
  useEffect(() => {
    if (!client) return;

    const onConnectionChange = (payload: any) => {
      if (payload?.state !== "Closed") return;

      joinAttemptedRef.current = false;
      attachedSelfElementRef.current = null;

      attachedRemotePlayersRef.current.clear();
      attachingRemoteUsersRef.current.clear();
      attachmentGenerationRef.current.clear();
      initialRemoteRefreshScheduledRef.current = false;
      forceRemoteReattachIdsRef.current.clear();

      attachRetryTimersRef.current.forEach(
        (timer) => {
          window.clearTimeout(timer);
        }
      );

      attachRetryTimersRef.current.clear();
      attachRetryCountRef.current.clear();
    };

    client.on(
      "connection-change",
      onConnectionChange
    );

    return () => {
      client.off(
        "connection-change",
        onConnectionChange
      );
    };
  }, [client]);

  /*
   * Automatically join if the user opens /consult/:cid directly.
   */
  useEffect(() => {
    if (!cid) return;

    if (isConsultEnded(cid)) {
      navigate("/", { replace: true });
      return;
    }

    if (status === "joined") return;
    if (joinAttemptedRef.current) return;

    if (
      !effectiveMeetingConfig ||
      !effectiveRoomCache
    ) {
      return;
    }

    if (!effectiveMeetingConfig.signature) {
      message.error(
        "Missing Zoom signature. Please refresh the link."
      );

      setShowLoading(false);
      return;
    }

    joinAttemptedRef.current = true;
    setShowLoading(true);

    void (async () => {
      try {
        const guestAttendee =
          readGuestAttendee(cid);

        const displayName = guestAttendee
          ? `${guestAttendee.name} (${guestAttendee.relationship})`
          : effectiveMeetingConfig.name;

        await joinOnce?.({
          topic:
            effectiveRoomCache.roomCacheId,
          name: displayName,
          signature:
            effectiveMeetingConfig.signature,
          password:
            effectiveMeetingConfig.password,
          webEndpoint:
            effectiveMeetingConfig.webEndpoint,
        });
      } catch (error) {
        console.warn("joinOnce failed", error);

        message.error(
          "Failed to join consult. Please refresh and try again."
        );

        joinAttemptedRef.current = false;
        setShowLoading(false);

        navigate(`/${cid}`);
      }
    })();
  }, [
    cid,
    effectiveMeetingConfig,
    effectiveRoomCache,
    joinOnce,
    navigate,
    readGuestAttendee,
    setShowLoading,
    status,
  ]);

  /*
   * Start the selected camera and audio after joining.
   */
  useEffect(() => {
    if (
      status !== "joined" ||
      !mediaStream ||
      !client
    ) {
      return;
    }

    let cancelled = false;

    const startLocalCamera = async () => {
      if (cancelled) return false;

      if (
        client.getCurrentUserInfo?.()?.bVideoOn
      ) {
        return true;
      }

      const availableCameras =
        (mediaStream.getCameraList?.() ||
          []) as CameraDevice[];

      const savedCamera:
        | CameraDevice
        | undefined = initialPrefs.cameraId
        ? availableCameras.find(
            (camera) =>
              camera.deviceId ===
              initialPrefs.cameraId
          )
        : undefined;

      const frontCamera:
        | CameraDevice
        | undefined =
        availableCameras.find(isFrontCamera);
      
      const mobile = isMobileDevice();

      const selectedCameraId: string | undefined =
        mobile
          ? "user"
          : savedCamera?.deviceId ??
            frontCamera?.deviceId;

      try {
        const options: {
          cameraId?: string;
          hd?: boolean;
          fullHd?: boolean;
        } = {
          hd: !mobile,
          fullHd: false,
        };

        if (selectedCameraId) {
          options.cameraId = selectedCameraId;
        } else if (mobile) {
          options.cameraId = "user";
        }

        await mediaStream.startVideo(options);
        await delay(250);

        const started =
          !!client.getCurrentUserInfo?.()
            ?.bVideoOn;

        if (started) {
          const activeCameraId =
            mediaStream.getActiveCamera?.();

          saveDevicePrefs({
            videoOn: true,
            ...(activeCameraId
              ? { cameraId: activeCameraId }
              : {}),
          });
        }

        return started;
      } catch (error) {
        console.warn(
          "Camera start attempt failed",
          error
        );

        return false;
      }
    };

    void (async () => {
      try {
        await delay(250);

        if (cancelled) return;

        if (initialPrefs.videoOn !== false) {
          let cameraStarted =
            await startLocalCamera();

          if (
            !cameraStarted &&
            !cancelled
          ) {
            await delay(750);
            cameraStarted =
              await startLocalCamera();
          }

          if (
            !cameraStarted &&
            !cancelled
          ) {
            try {
              await mediaStream.startVideo();
              await delay(250);

              cameraStarted =
                !!client.getCurrentUserInfo?.()
                  ?.bVideoOn;
            } catch (error) {
              console.warn(
                "Final camera start attempt failed",
                error
              );
            }
          }

          if (!cameraStarted) {
            console.warn(
              "Camera could not be auto-started. Browser interaction may be required."
            );
          }
        }

        if (cancelled) return;

        if (initialPrefs.speakerId) {
          try {
            await mediaStream.switchSpeaker(
              initialPrefs.speakerId
            );
          } catch (error) {
            console.warn(
              "Saved speaker could not be selected",
              error
            );
          }
        }

        if (initialPrefs.micId) {
          try {
            await mediaStream.switchMicrophone(
              initialPrefs.micId
            );
          } catch (error) {
            console.warn(
              "Saved microphone could not be selected",
              error
            );
          }
        }

        try {
          const hasAudio =
            !!client.getCurrentUserInfo?.()?.audio;

          if (!hasAudio) {
            await mediaStream.startAudio({
              highBitrate: true,
            });
          }

          await delay(500);

          if (cancelled) return;

          const shouldBeMuted =
            initialPrefs.micMuted === true;

          if (shouldBeMuted) {
            await mediaStream.muteAudio();
          } else {
            await mediaStream.unmuteAudio();

            window.setTimeout(() => {
              if (cancelled) return;

              void (async () => {
                try {
                  const stillMuted =
                    !!client.getCurrentUserInfo?.()
                      ?.muted;

                  if (stillMuted) {
                    await mediaStream.unmuteAudio();
                  }
                } catch (error) {
                  console.warn(
                    "Second unmute attempt failed",
                    error
                  );
                }
              })();
            }, 750);
          }
        } catch (error) {
          console.warn(
            "Automatic audio startup failed",
            error
          );
        }
      } finally {
        if (!cancelled) {
          setShowLoading?.(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    client,
    initialPrefs,
    mediaStream,
    setShowLoading,
    status,
  ]);

  useEffect(() => {
    if (status !== "joined" || !mediaStream || !client) {
      return;
    }

    const logDiagnostics = (reason: string) => {
      const requirements = ZoomVideo.checkSystemRequirements();
      const allUsers = (client.getAllUser?.() || []) as Participant[];

      const diagnostics = {
        reason,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
        isIOS: isIOSDevice(),
        isMobile: isMobileDevice(),
        visibilityState: document.visibilityState,
        crossOriginIsolated: window.crossOriginIsolated,
        sharedArrayBufferAvailable:
          typeof SharedArrayBuffer !== "undefined",
        webAssemblyAvailable:
          typeof WebAssembly !== "undefined",
        systemRequirements: requirements,
        supportsMultipleVideos:
          mediaStream.isSupportMultipleVideos?.(),
        cameraList: mediaStream.getCameraList?.(),
        activeCamera: mediaStream.getActiveCamera?.(),
        currentUser: client.getCurrentUserInfo?.(),
        sessionInfo: client.getSessionInfo?.(),
        allUsers: allUsers.map((user) => ({
          userId: user.userId,
          displayName: user.displayName,
          bVideoOn: user.bVideoOn,
        })),
        attachedRemoteUserIds: Array.from(
          attachedRemotePlayersRef.current.keys()
        ),
        attachingRemoteUserIds: Array.from(
          attachingRemoteUsersRef.current.values()
        ),
      };

      console.log("[Zoom diagnostics]", diagnostics);
    };

    const onUserAdded = (payload: any) => {
      console.log("[Zoom event user-added]", payload);
      window.setTimeout(() => logDiagnostics("user-added"), 250);
    };

    const onUserRemoved = (payload: any) => {
      console.log("[Zoom event user-removed]", payload);
      window.setTimeout(() => logDiagnostics("user-removed"), 0);
    };

    const onUserUpdated = (payload: any) => {
      console.log("[Zoom event user-updated]", payload);
    };

    const onPeerVideoStateChange = (payload: any) => {
      console.log("[Zoom event peer-video-state-change]", payload);
      window.setTimeout(
        () => logDiagnostics("peer-video-state-change"),
        250
      );
    };

    const onConnectionChange = (payload: any) => {
      console.log("[Zoom event connection-change]", payload);
    };

    const onVideoCapturingChange = (payload: any) => {
      console.log("[Zoom event video-capturing-change]", payload);
    };

    const onVisibilityChange = () => {
      console.log("[Browser visibility-change]", {
        visibilityState: document.visibilityState,
      });
      logDiagnostics("visibility-change");
    };

    logDiagnostics("joined");

    client.on("user-added", onUserAdded);
    client.on("user-removed", onUserRemoved);
    client.on("user-updated", onUserUpdated);
    client.on("peer-video-state-change", onPeerVideoStateChange);
    client.on("connection-change", onConnectionChange);
    client.on("video-capturing-change", onVideoCapturingChange);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      client.off("user-added", onUserAdded);
      client.off("user-removed", onUserRemoved);
      client.off("user-updated", onUserUpdated);
      client.off("peer-video-state-change", onPeerVideoStateChange);
      client.off("connection-change", onConnectionChange);
      client.off("video-capturing-change", onVideoCapturingChange);
      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange
      );
    };
  }, [status, mediaStream, client]);

  /*
   * Synchronise local camera state.
   */
  useEffect(() => {
    if (!client) return;

    const syncSelfVideo = () => {
      const currentUser =
        client.getCurrentUserInfo?.();

      setSelfVideoOn(
        !!currentUser?.bVideoOn
      );
    };

    syncSelfVideo();

    client.on(
      "user-updated",
      syncSelfVideo
    );

    client.on(
      "video-capturing-change",
      syncSelfVideo
    );

    return () => {
      client.off(
        "user-updated",
        syncSelfVideo
      );

      client.off(
        "video-capturing-change",
        syncSelfVideo
      );
    };
  }, [client]);

  /*
   * Synchronise participant presence and remote camera state.
   */
  useEffect(() => {
    if (!client) return;

    const syncParticipants = () => {
      const allUsers =
        (client.getAllUser?.() || []) as Participant[];

      const currentUserId =
        client.getSessionInfo?.()?.userId;

      const nextRemotes = allUsers
        .filter((user) => user.userId !== currentUserId)
        .map((user) => ({
          userId: user.userId,
          displayName: user.displayName,
          bVideoOn: !!user.bVideoOn,
        }))
        .sort(
          (a, b) =>
            Number(b.bVideoOn) - Number(a.bVideoOn)
        );

      setRemotes((currentRemotes) => {
        const unchanged =
          currentRemotes.length === nextRemotes.length &&
          currentRemotes.every((current, index) => {
            const next = nextRemotes[index];

            return (
              current.userId === next.userId &&
              current.displayName === next.displayName &&
              !!current.bVideoOn === !!next.bVideoOn
            );
          });

        return unchanged ? currentRemotes : nextRemotes;
      });
    };

    const onUserUpdated = (payload: any) => {
      const updates = Array.isArray(payload)
        ? payload
        : [payload];

      syncParticipants();

      updates.forEach((update) => {
        const userId = Number(update?.userId);

        if (!Number.isFinite(userId)) return;
        if (userId === selfId) return;

        const user = (
          client.getAllUser?.() || []
        ).find(
          (item: Participant) =>
            item.userId === userId
        ) as Participant | undefined;

        const alreadyAttached =
          attachedRemotePlayersRef.current.has(userId);

        if (user?.bVideoOn && !alreadyAttached) {
          clearAttachRetry(userId);

          setAttachRetryVersion(
            (version) => version + 1
          );
        }
      });
    };

    const onPeerVideoStateChange = (
      payload: any
    ) => {
      syncParticipants();

      window.setTimeout(() => {
        if (!mountedRef.current) return;

        syncParticipants();

        const userId = Number(payload?.userId);

        if (Number.isFinite(userId)) {
          clearAttachRetry(userId);
        }

        setAttachRetryVersion(
          (version) => version + 1
        );
      }, 0);
    };

    syncParticipants();

    client.on(
      "user-added",
      syncParticipants
    );

    client.on(
      "user-removed",
      syncParticipants
    );

    client.on(
      "user-updated",
      onUserUpdated
    );

    client.on(
      "peer-video-state-change",
      onPeerVideoStateChange
    );

    return () => {
      client.off(
        "user-added",
        syncParticipants
      );

      client.off(
        "user-removed",
        syncParticipants
      );

      client.off(
        "user-updated",
        onUserUpdated
      );

      client.off(
        "peer-video-state-change",
        onPeerVideoStateChange
      );
    };
  }, [clearAttachRetry, client, selfId]);

  /*
   * Safari can report existing participants immediately after join while its
   * media/rendering layer is still settling. These staged pulses are safe:
   * already-attached players are ignored, while missing/stale attachments get
   * another opportunity after both React and Zoom are ready.
   */
  useEffect(() => {
    if (status !== "joined" || !mediaStream || remotes.length === 0) {
      return;
    }

    const delays = isIOSDevice()
      ? [100, 400, 1000]
      : [100, 500];

    const timers = delays.map((milliseconds) =>
      window.setTimeout(() => {
        if (!mountedRef.current) return;

        console.log("[Zoom staged attachment pulse]", {
          milliseconds,
          remoteUserIds: remotes.map((user) => user.userId),
        });

        setAttachRetryVersion((version) => version + 1);
      }, milliseconds)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [mediaStream, remotes.length, status]);

  /*
   * Existing participants may already have video enabled when this device
   * joins. On some browsers Zoom creates the player successfully before the
   * receiving subscription has fully settled, leaving a blank player until
   * the sender toggles their camera.
   *
   * Perform one delayed forced detach/reattach for only those participants
   * who were already video-on during the initial join. This mirrors the
   * successful camera off/on recovery without affecting later joiners.
   */
  useEffect(() => {
    if (
      status !== "joined" ||
      !mediaStream ||
      initialRemoteRefreshScheduledRef.current
    ) {
      return;
    }

    const existingVideoUserIds = remotes
      .filter((user) => user.bVideoOn)
      .map((user) => user.userId);

    if (existingVideoUserIds.length === 0) {
      return;
    }

    initialRemoteRefreshScheduledRef.current = true;

    const runRefresh = (delayMs: number) => {
      return window.setTimeout(() => {
        if (!mountedRef.current) return;

        const currentUsers =
          (client.getAllUser?.() || []) as Participant[];

        const stillActiveIds = existingVideoUserIds.filter((userId) =>
          currentUsers.some(
            (user) => user.userId === userId && !!user.bVideoOn
          )
        );

        if (stillActiveIds.length === 0) return;

        stillActiveIds.forEach((userId) => {
          forceRemoteReattachIdsRef.current.add(userId);
          clearAttachRetry(userId);
        });

        console.log("[Zoom initial remote subscription refresh]", {
          delayMs,
          userIds: stillActiveIds,
        });

        setInitialRemoteRefreshVersion((version) => version + 1);
      }, delayMs);
    };

    const timers = [700, 1600].map(runRefresh);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [
    clearAttachRetry,
    client,
    mediaStream,
    remotes,
    status,
  ]);

  /*
   * Attach and detach self and remote video streams.
   */
  useEffect(() => {
    if (!mediaStream || !selfId) {
      return;
    }

    let cancelled = false;

    const attachSelfVideo = async () => {
      const selfElement =
        playerRefs.current[String(selfId)];

      if (!selfVideoOn) {
        const attachedElement =
          attachedSelfElementRef.current;

        if (attachedElement) {
          attachedSelfElementRef.current = null;

          try {
            await detachVideoFromElement(
              selfId,
              attachedElement
            );
          } catch (error) {
            console.warn(
              "Failed to detach self video",
              error
            );
          }
        }

        return;
      }

      if (!selfElement) return;

      if (
        attachedSelfElementRef.current ===
        selfElement
      ) {
        return;
      }

      const oldElement =
        attachedSelfElementRef.current;

      if (
        oldElement &&
        oldElement !== selfElement
      ) {
        attachedSelfElementRef.current = null;

        try {
          await detachVideoFromElement(
            selfId,
            oldElement
          );
        } catch (error) {
          console.warn(
            "Failed to detach old self video player",
            error
          );
        }
      }

      try {
        await attachSelfVideoToElement(
          selfId,
          VideoQuality.Video_360P,
          selfElement
        );

        const currentElement =
          playerRefs.current[String(selfId)];

        if (
          !cancelled &&
          currentElement === selfElement
        ) {
          attachedSelfElementRef.current =
            selfElement;
        } else {
          await detachVideoFromElement(
            selfId,
            selfElement
          );
        }
      } catch (error) {
        console.warn(
          "Failed to attach self video",
          error
        );
      }
    };

    const detachStaleRemoteVideos = async () => {
      const remoteById = new Map(
        remotes.map((user) => [
          user.userId,
          user,
        ])
      );

      const attachments = Array.from(
        attachedRemotePlayersRef.current.entries()
      );

      for (const [userId, player] of attachments) {
        const currentRemote = remoteById.get(userId);
        const currentTile =
          remoteTileRefsRef.current.get(userId);

        const playerStillInCurrentTile =
          !!currentTile &&
          player.parentElement === currentTile;

        const shouldDetach =
          !currentRemote ||
          !currentRemote.bVideoOn ||
          !currentTile ||
          !currentTile.isConnected ||
          !playerStillInCurrentTile;

        if (!shouldDetach) continue;

        incrementAttachmentGeneration(userId);
        attachedRemotePlayersRef.current.delete(userId);
        attachingRemoteUsersRef.current.delete(userId);

        try {
          await detachVideoFromElement(userId, player);
        } catch (error) {
          console.warn(
            "Failed to detach stale remote video",
            userId,
            error
          );
        }

        try {
          player.remove();
        } catch {
          // Best-effort DOM cleanup.
        }
      }
    };

    const attachRemoteVideo = async (
      remoteUser: Participant
    ) => {
      const userId = remoteUser.userId;

      if (cancelled || !remoteUser.bVideoOn) {
        return;
      }

      const tile =
        remoteTileRefsRef.current.get(userId);

      if (!tile || !tile.isConnected) {
        scheduleAttachRetry(userId);
        return;
      }

      const existingPlayer =
        attachedRemotePlayersRef.current.get(userId);

      const forceReattach =
        forceRemoteReattachIdsRef.current.has(userId);

      if (
        existingPlayer &&
        existingPlayer.parentElement === tile &&
        existingPlayer.isConnected &&
        !forceReattach
      ) {
        clearAttachRetry(userId);
        return;
      }

      if (
        attachingRemoteUsersRef.current.has(userId)
      ) {
        return;
      }

      if (existingPlayer) {
        incrementAttachmentGeneration(userId);
        attachedRemotePlayersRef.current.delete(userId);

        try {
          await detachVideoFromElement(
            userId,
            existingPlayer
          );
        } catch (error) {
          console.warn(
            "Failed to detach previous remote player",
            userId,
            error
          );
        }

        try {
          existingPlayer.remove();
        } catch {
          // Best-effort DOM cleanup.
        }

        if (forceReattach) {
          // Give Zoom a short gap to release the stale receiving subscription
          // before requesting the same participant stream again.
          await delay(120);
        }
      }

      const generation =
        incrementAttachmentGeneration(userId);

      attachingRemoteUsersRef.current.add(userId);

      try {
        const quality =
          remotes.length === 1
            ? VideoQuality.Video_720P
            : VideoQuality.Video_360P;

        const player = await attachRemoteVideoPlayer(
          userId,
          quality
        );

        if (!player) {
          scheduleAttachRetry(userId);
          return;
        }

        const latestTile =
          remoteTileRefsRef.current.get(userId);

        const currentGeneration =
          attachmentGenerationRef.current.get(userId);

        const latestParticipant =
          (client.getAllUser?.() || []).find(
            (user: Participant) => user.userId === userId
          ) as Participant | undefined;

        const attachmentIsCurrent =
          currentGeneration === generation &&
          latestTile === tile &&
          tile.isConnected &&
          !!latestParticipant?.bVideoOn;

        if (!attachmentIsCurrent) {
          try {
            await detachVideoFromElement(userId, player);
          } catch {
            // Best-effort stale attachment cleanup.
          }

          try {
            player.remove();
          } catch {
            // Best-effort DOM cleanup.
          }

          return;
        }

        Object.assign(player.style, {
          width: "100%",
          height: "100%",
          display: "block",
          objectFit:
            remotes.length === 1
              ? remoteFit
              : "contain",
        });

        /*
         * Zoom owns the custom element. React only owns this stable mount div.
         * This avoids Safari initialising an already-created video-player too
         * early when joining a consult with existing participants.
         */
        tile.replaceChildren(player);

        attachedRemotePlayersRef.current.set(
          userId,
          player
        );

        forceRemoteReattachIdsRef.current.delete(userId);

        console.log("[Zoom remote player appended]", {
          userId,
          tileConnected: tile.isConnected,
          tileWidth: tile.clientWidth,
          tileHeight: tile.clientHeight,
          playerConnected: player.isConnected,
          playerWidth: player.clientWidth,
          playerHeight: player.clientHeight,
        });

        clearAttachRetry(userId);
      } catch (error) {
        console.warn(
          "Failed to attach remote video",
          userId,
          error
        );

        scheduleAttachRetry(userId);
      } finally {
        attachingRemoteUsersRef.current.delete(userId);
      }
    };

    void (async () => {
      await detachStaleRemoteVideos();

      if (cancelled) return;

      await attachSelfVideo();

      if (cancelled) return;

      const videoRemotes = remotes.filter(
        (user) => user.bVideoOn
      );

      const supportsMultipleVideos =
        mediaStream.isSupportMultipleVideos?.();

      console.log("[Zoom remote attachment plan]", {
        supportsMultipleVideos,
        isIOS: isIOSDevice(),
        remoteCount: remotes.length,
        videoRemoteCount: videoRemotes.length,
        remoteUserIds: videoRemotes.map((user) => user.userId),
      });

      /*
       * Attach sequentially rather than with Promise.all.
       * This reduces simultaneous decoder pressure on Safari and makes the
       * iPhone logs show exactly which attachment succeeds or fails.
       */
      for (const remoteUser of videoRemotes) {
        if (cancelled) break;
        await attachRemoteVideo(remoteUser);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    attachRetryVersion,
    initialRemoteRefreshVersion,
    playerMountVersion,
    attachSelfVideoToElement,
    attachRemoteVideoPlayer,
    clearAttachRetry,
    client,
    detachVideoFromElement,
    incrementAttachmentGeneration,
    mediaStream,
    remotes,
    scheduleAttachRetry,
    selfId,
    selfVideoOn,
  ]);

  /*
   * Keep Zoom-created remote players visually aligned with the current
   * single/grid layout without recreating or reattaching them.
   */
  useEffect(() => {
    attachedRemotePlayersRef.current.forEach((player) => {
      Object.assign(player.style, {
        width: "100%",
        height: "100%",
        display: "block",
        objectFit:
          remotes.length === 1
            ? remoteFit
            : "contain",
      });
    });
  }, [remoteFit, remotes.length]);

  /*
   * Final stream cleanup when leaving this screen.
   */
  useEffect(() => {
    return () => {
      attachRetryTimersRef.current.forEach(
        (timer) => {
          window.clearTimeout(timer);
        }
      );

      attachRetryTimersRef.current.clear();
      attachRetryCountRef.current.clear();

      const selfElement =
        attachedSelfElementRef.current;

      if (selfId && selfElement) {
        void detachVideoFromElement(
          selfId,
          selfElement
        ).catch(() => {
          // Best-effort unmount cleanup.
        });
      }

      attachedSelfElementRef.current = null;

      Array.from(
        attachedRemotePlayersRef.current.entries()
      ).forEach(([userId, player]) => {
        void detachVideoFromElement(
          userId,
          player
        ).catch(() => {
          // Best-effort unmount cleanup.
        });

        try {
          player.remove();
        } catch {
          // Best-effort DOM cleanup.
        }
      });

      attachedRemotePlayersRef.current.clear();
      remoteTileRefsRef.current.clear();
      attachingRemoteUsersRef.current.clear();
      attachmentGenerationRef.current.clear();
    };
  }, [detachVideoFromElement, selfId]);


  const copyDebugLogs = useCallback(async () => {
    const text = debugLogs.length
      ? debugLogs.join("\n\n----------------------------------------\n\n")
      : "No diagnostics have been captured yet.";

    try {
      await navigator.clipboard.writeText(text);
      message.success("Diagnostics copied");
    } catch {
      window.prompt("Copy diagnostics:", text);
    }
  }, [debugLogs]);

  if (
    status !== "joined" ||
    !mediaStream ||
    !client ||
    !selfId
  ) {
    return (
      <div
        style={{
          padding: 16,
          color: "white",
        }}
      >
        Joining…
      </div>
    );
  }

  const canControlRemoteView = remotes.length === 1;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "black",
      }}

    >
      {debugEnabled && (
        <div
          style={{
            position: "fixed",
            left: 8,
            right: 8,
            top: 8,
            maxHeight: debugPanelCollapsed ? 48 : "46vh",
            overflow: "hidden",
            zIndex: 99999,
            borderRadius: 10,
            background: "rgba(0, 0, 0, 0.94)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.25)",
            boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
            fontSize: 11,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              minHeight: 46,
              padding: "6px 8px",
              borderBottom: debugPanelCollapsed
                ? "none"
                : "1px solid rgba(255,255,255,0.18)",
            }}
          >
            <strong>
              Zoom Diagnostics ({debugLogs.length})
            </strong>

            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setDebugPanelCollapsed((value) => !value)}
                style={{ padding: "6px 8px" }}
              >
                {debugPanelCollapsed ? "Expand" : "Collapse"}
              </button>

              <button
                type="button"
                onClick={() => void copyDebugLogs()}
                style={{ padding: "6px 8px" }}
              >
                Copy
              </button>

              <button
                type="button"
                onClick={() => setDebugLogs([])}
                style={{ padding: "6px 8px" }}
              >
                Clear
              </button>
            </div>
          </div>

          {!debugPanelCollapsed && (
            <div
              style={{
                maxHeight: "calc(46vh - 48px)",
                overflowY: "auto",
                padding: 8,
                WebkitOverflowScrolling: "touch",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {debugLogs.length
                  ? debugLogs.join("\n\n----------------------------------------\n\n")
                  : "Waiting for Zoom diagnostics…"}
              </pre>
            </div>
          )}
        </div>
      )}

      <video-player-container
        style={layout.stageStyle as any}
      >
        <div
          ref={remoteStageRef}
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            touchAction:
              canControlRemoteView &&
              remoteZoom > 1
                ? "none"
                : "auto",
          }}
        >
          {remotes.length > 0 ? (
            <div style={getRemoteGridStyle(remotes.length)}>
              {remotes.map((remoteUser) => {
                const isSingleRemote = remotes.length === 1;

                return (
                  <div
                    key={remoteUser.userId}
                    onPointerDown={
                      isSingleRemote
                        ? onRemotePointerDown
                        : undefined
                    }
                    onPointerMove={
                      isSingleRemote
                        ? onRemotePointerMove
                        : undefined
                    }
                    onPointerUp={
                      isSingleRemote
                        ? endRemoteDrag
                        : undefined
                    }
                    onPointerCancel={
                      isSingleRemote
                        ? endRemoteDrag
                        : undefined
                    }
                    onLostPointerCapture={
                      isSingleRemote
                        ? endRemoteDrag
                        : undefined
                    }
                    style={{
                      position: "relative",
                      background: "#111",
                      borderRadius: isSingleRemote ? 0 : 12,
                      overflow: "hidden",
                      minWidth: 0,
                      minHeight: 0,
                      width: "100%",
                      height: "100%",
                      touchAction:
                        isSingleRemote && remoteZoom > 1
                          ? "none"
                          : "auto",
                      cursor:
                        isSingleRemote && remoteZoom > 1
                          ? "grab"
                          : "default",
                    }}
                  >
                    {remoteUser.bVideoOn ? (
                      <RemoteVideoMount
                        userId={remoteUser.userId}
                        register={registerRemoteTileRef}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: isSingleRemote
                            ? remoteFit
                            : "contain",
                          display: "block",
                          transform: isSingleRemote
                            ? `translate(${remotePan.x}px, ${remotePan.y}px) scale(${remoteZoom})`
                            : "none",
                          transformOrigin: "center center",
                          transition:
                            isSingleRemote &&
                            !dragRef.current?.active
                              ? "transform 120ms ease"
                              : "none",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          color: "white",
                          display: "flex",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          padding: 16,
                        }}
                      >
                        {remoteUser.displayName ||
                          "Participant"}{" "}
                        has camera off
                      </div>
                    )}

                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        bottom: 10,
                        padding: "4px 8px",
                        borderRadius: 8,
                        background: "rgba(0,0,0,0.6)",
                        color: "white",
                        fontSize: 13,
                        pointerEvents: "none",
                      }}
                    >
                      {remoteUser.displayName ||
                        "Participant"}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                color: "white",
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Waiting for the other participant…
            </div>
          )}
        </div>

        <div style={layout.pipStyle}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              borderRadius: 12,
              background: "transparent",
            }}
          >
            <video-player
              ref={registerSelfPlayerRef}
              style={
                layout.pipInnerStyle as any
              }
            />
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 10,
            display: "flex",
            justifyContent: "center",
            zIndex: 6,
          }}
        >
          <VideoFooter />
        </div>
      </video-player-container>

      {canControlRemoteView && (
        <div
          style={{
            position: "absolute",
            left: 10,
            top: 10,
            display: "flex",
            justifyContent: "flex-start",
            zIndex: 20,
          }}
        >
          <RemoteViewControl />
        </div>
      )}
    </div>
  );
}